import React, { useState } from 'react';
import { Check, X, ArrowRight, Flame, Clock, BookOpen, ChevronRight } from 'lucide-react';
const CtaButton = ({
  text,
  subtext,
  href
}: {
  text: string;
  subtext?: string;
  href?: string;
}) => <div className="flex flex-col items-center w-full max-w-xl mx-auto mt-8" data-id="element-0">
    {href ? <a href={href} target="_blank" rel="noopener noreferrer" className="w-full bg-forest hover:bg-[#244012] text-white font-bold py-4 px-6 md:px-8 rounded-xl shadow-xl transition-all transform hover:-translate-y-1 hover:shadow-2xl flex items-center justify-center gap-3 text-base md:text-lg group no-underline" data-id="element-1">
        <span className="flex-1 text-center" data-id="element-2">{text}</span>
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-3" />
      </a> : <button onClick={() => document.getElementById('offer')?.scrollIntoView({
    behavior: 'smooth'
  })} className="w-full bg-forest hover:bg-[#244012] text-white font-bold py-4 px-6 md:px-8 rounded-xl shadow-xl transition-all transform hover:-translate-y-1 hover:shadow-2xl flex items-center justify-center gap-3 text-base md:text-lg group" data-id="element-4">
        <span className="flex-1 text-center" data-id="element-5">{text}</span>
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-6" />
      </button>}
    {subtext && <p className="text-sm text-gray-600 mt-4 text-center font-medium" data-id="element-7">
        {subtext}
      </p>}
  </div>;
const privacyContent = `
Last updated: April 2, 2026

Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your personal information when you purchase and use "The 15-Minute Metabolic Reset."

**Information We Collect**
When you make a purchase, we collect your name, email address, and payment information. Payment processing is handled by secure third-party providers — we never store your credit card details.

**How We Use Your Information**
We use your information solely to deliver your digital product, send purchase confirmations, and provide customer support. We do not sell, rent, or share your personal data with third parties for marketing purposes.

**Cookies**
Our website may use essential cookies to ensure proper functionality. We do not use tracking cookies for advertising.

**Data Security**
We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, or destruction.

**Your Rights**
You may request access to, correction of, or deletion of your personal data at any time by contacting us.

**Changes to This Policy**
We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
`.trim();
const termsContent = `
Last updated: April 2, 2026

By purchasing "The 15-Minute Metabolic Reset," you agree to the following terms and conditions.

**Digital Product**
This is a digital eBook delivered instantly via download. No physical product will be shipped.

**Personal Use Only**
This eBook is licensed for your personal, non-commercial use only. You may not redistribute, resell, copy, or share the content in any form.

**Health Disclaimer**
The information provided in this eBook is for educational purposes only and is not intended as medical advice. Always consult a qualified healthcare professional before starting any new dietary program, especially if you have pre-existing health conditions. Results may vary from person to person.

**No Guarantees**
While the methods described are based on nutritional principles, we make no guarantees regarding specific weight loss or health outcomes.

**Refund Policy**
Due to the digital nature of this product, all sales are final. If you experience technical issues with your download, please contact us and we will resolve the issue promptly.

**Limitation of Liability**
We are not liable for any damages arising from the use or inability to use the information in this eBook.

**Governing Law**
These terms are governed by applicable local laws. Any disputes will be resolved through appropriate legal channels.
`.trim();
function Modal({
  title,
  content,
  onClose
}: {
  title: string;
  content: string;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-8">
      <div className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm" data-id="element-9" />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-10" onClick={e => e.stopPropagation()} data-id="element-10">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-11">
          <X className="w-5 h-5 text-gray-500" data-id="element-12" />
        </button>
        <h2 className="font-playfair text-3xl font-bold text-charcoal mb-6" data-id="element-13">
          {title}
        </h2>
        <div className="prose prose-lg text-gray-700 leading-relaxed whitespace-pre-line text-base" data-id="element-14">
          {content.split('\n').map((line, i) => {
          if (line.startsWith('**') && line.endsWith('**')) {
            return <h3 key={i} className="font-bold text-charcoal text-lg mt-6 mb-2" data-id="element-15">
                  {line.replace(/\*\*/g, '')}
                </h3>;
          }
          return line ? <p key={i} className="mb-3" data-id="element-16">
                {line}
              </p> : null;
        })}
        </div>
      </div>
    </div>;
}
export function App() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen selection:bg-forest selection:text-white" data-id="element-17">
      {/* 1. HERO SECTION */}
      <section className="pt-16 pb-20 px-4 md:px-8 max-w-5xl mx-auto text-center" data-id="element-18">
        <div className="inline-block mb-4 px-4 py-1.5 bg-terracotta/10 text-terracotta font-semibold rounded-full text-sm tracking-wide uppercase" data-id="element-19">
          For Women 35-60
        </div>
        <h1 className="font-playfair text-5xl md:text-7xl font-bold text-charcoal leading-[1.1] mb-6" data-id="element-20">
          The 15-Minute
          <br data-id="element-21" />
          Metabolic Reset
        </h1>
        <h2 className="text-xl md:text-2xl text-gray-700 mb-12 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-22">
          A Simple 14-Day Blueprint to Melt Mid-Life Bloat Without Counting
          Calories or Cooking for Hours
        </h2>

        <div className="relative w-full max-w-3xl mx-auto mb-16 rounded-2xl overflow-hidden shadow-2xl border-8 border-white" data-id="element-23">
          <img src="https://cdn.magicpatterns.com/uploads/1g6xgsLjt8TNdY77ZwHgn2/Untitled_Project_(1).png" alt="Healthy metabolic reset bowl" className="w-full h-auto object-cover" data-id="element-24" />
        </div>

        <div className="max-w-2xl mx-auto bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-gray-100" data-id="element-25">
          <p className="text-xl md:text-2xl font-medium text-charcoal mb-8 leading-relaxed" data-id="element-26">
            It's that moment: your favorite jeans won't button. You're eating
            healthy, you're staying active, but your body feels stuck.
          </p>
          <CtaButton text="GET YOUR 15-MINUTE METABOLIC RESET NOW" price="$6.36" subtext="Instant digital download • One-time payment • No subscriptions" data-id="element-27" />
        </div>
      </section>

      {/* 2. PROBLEM SECTION */}
      <section className="bg-charcoal text-cream py-24 px-4 md:px-8" data-id="element-28">
        <div className="max-w-4xl mx-auto text-center" data-id="element-29">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-8 leading-tight" data-id="element-30">
            Most Plans Ignore How a Woman's Metabolism Changes After 35
          </h2>
          <p className="text-2xl md:text-3xl font-light text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-31">
            Dieting harder backfires, leaving you bloated, tired, and
            frustrated.
          </p>
        </div>
      </section>

      {/* 3. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-24 px-4 md:px-8 max-w-5xl mx-auto" data-id="element-32">
        <h2 className="font-playfair text-4xl md:text-5xl font-bold text-center mb-16 text-charcoal" data-id="element-33">
          Why What You Tried Didn't Work
        </h2>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-id="element-34">
          {['Low-calorie diets crush your thyroid.', 'Excessive cardio spikes cortisol and belly fat.', 'Complex recipes take too long—consistency fails.', '"Healthy" meals often hide extra calories.'].map((item, i) => <div key={i} className="flex items-start gap-5 p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-35">
              <div className="bg-terracotta/10 p-3 rounded-full shrink-0" data-id="element-36">
                <X className="w-6 h-6 text-terracotta" strokeWidth={3} data-id="element-37" />
              </div>
              <p className="text-xl text-gray-800 font-medium pt-1 leading-relaxed" data-id="element-38">
                {item}
              </p>
            </div>)}
        </div>
      </section>

      {/* 4. UNIQUE MECHANISM */}
      <section className="bg-softGreen py-24 px-4 md:px-8" data-id="element-39">
        <div className="max-w-4xl mx-auto text-center" data-id="element-40">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-forest text-white rounded-full mb-8 shadow-lg" data-id="element-41">
            <Flame className="w-8 h-8" data-id="element-42" />
          </div>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-forest mb-8" data-id="element-43">
            Introducing the Thermal Assembly Method
          </h2>
          <p className="text-2xl text-gray-800 max-w-3xl mx-auto leading-relaxed font-light" data-id="element-44">
            A 15-minute "no-cook" meal blueprint. By combining specific
            high-thermal ingredients, you force your body to burn more energy
            just to digest—
            <strong className="font-semibold" data-id="element-45">
              no complicated cooking required.
            </strong>
          </p>
        </div>
      </section>

      {/* 5. WHAT YOU GET */}
      <section className="py-24 px-4 md:px-8 max-w-4xl mx-auto" data-id="element-46">
        <h2 className="font-playfair text-4xl md:text-5xl font-bold text-center mb-16 text-charcoal" data-id="element-47">
          Everything You Need to Succeed
        </h2>
        <div className="space-y-4" data-id="element-48">
          {['The full 28-page digital eBook with the step-by-step 14-day plan', '28 simple "assemblies" (7 breakfasts, 7 lunches, 7 dinners, 7 snacks)', 'The 3-2-1 Plate Rule to balance every meal', 'The 15-Minute Kitchen Audit to stock your staples fast'].map((item, i) => <div key={i} className="flex items-center gap-5 p-6 md:p-8 bg-white rounded-2xl shadow-sm border border-gray-100" data-id="element-49">
              <div className="bg-forest/10 p-2 rounded-full shrink-0" data-id="element-50">
                <Check className="w-6 h-6 text-forest" strokeWidth={3} data-id="element-51" />
              </div>
              <p className="text-lg md:text-xl text-gray-800 font-medium" data-id="element-52">
                {item}
              </p>
            </div>)}
        </div>
      </section>

      {/* 6. WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 px-4 md:px-8 bg-white border-y border-gray-100" data-id="element-53">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-54">
          {/* For */}
          <div className="bg-softGreen/40 p-8 md:p-10 rounded-3xl border border-softGreen" data-id="element-55">
            <h3 className="font-playfair text-3xl font-bold text-forest mb-8 flex items-center gap-3" data-id="element-56">
              <Check className="w-8 h-8" data-id="element-57" /> Who This Is For
            </h3>
            <ul className="space-y-6" data-id="element-58">
              <li className="flex gap-4 items-start" data-id="element-59">
                <ChevronRight className="w-6 h-6 text-forest shrink-0 mt-0.5" data-id="element-60" />
                <span className="text-lg text-gray-800" data-id="element-61">
                  Women 35-60 battling stubborn midsection weight
                </span>
              </li>
              <li className="flex gap-4 items-start" data-id="element-62">
                <ChevronRight className="w-6 h-6 text-forest shrink-0 mt-0.5" data-id="element-63" />
                <span className="text-lg text-gray-800" data-id="element-64">
                  Busy women who want fast, effective meals
                </span>
              </li>
              <li className="flex gap-4 items-start" data-id="element-65">
                <ChevronRight className="w-6 h-6 text-forest shrink-0 mt-0.5" data-id="element-66" />
                <span className="text-lg text-gray-800" data-id="element-67">
                  Anyone who's tried "eating healthy" and still feels stuck
                </span>
              </li>
            </ul>
          </div>
          {/* Not For */}
          <div className="bg-red-50/50 p-8 md:p-10 rounded-3xl border border-red-100" data-id="element-68">
            <h3 className="font-playfair text-3xl font-bold text-terracotta mb-8 flex items-center gap-3" data-id="element-69">
              <X className="w-8 h-8" data-id="element-70" /> Who This Is Not For
            </h3>
            <ul className="space-y-6" data-id="element-71">
              <li className="flex gap-4 items-start" data-id="element-72">
                <div className="w-2 h-2 rounded-full bg-terracotta mt-2.5 shrink-0" data-id="element-73" />
                <span className="text-lg text-gray-800" data-id="element-74">
                  People wanting a quick-fix magic pill
                </span>
              </li>
              <li className="flex gap-4 items-start" data-id="element-75">
                <div className="w-2 h-2 rounded-full bg-terracotta mt-2.5 shrink-0" data-id="element-76" />
                <span className="text-lg text-gray-800" data-id="element-77">
                  Those unwilling to follow a structured plan for 14 days
                </span>
              </li>
              <li className="flex gap-4 items-start" data-id="element-78">
                <div className="w-2 h-2 rounded-full bg-terracotta mt-2.5 shrink-0" data-id="element-79" />
                <span className="text-lg text-gray-800" data-id="element-80">
                  Bodybuilders or extreme athletes
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 7. HOW IT WORKS */}
      <section className="py-24 px-4 md:px-8 max-w-5xl mx-auto" data-id="element-81">
        <h2 className="font-playfair text-4xl md:text-5xl font-bold text-center mb-20 text-charcoal" data-id="element-82">
          How It Works
        </h2>
        <div className="grid md:grid-cols-3 gap-12 relative" data-id="element-83">
          {/* Connective line for desktop */}
          <div className="hidden md:block absolute top-10 left-[16%] right-[16%] h-0.5 bg-gray-200 -z-10" data-id="element-84"></div>

          {[{
          step: '1',
          title: 'Stock',
          desc: 'Stock your kitchen with the right metabolic components'
        }, {
          step: '2',
          title: 'Assemble',
          desc: 'Follow the 15-minute assemblies each day using the 3-2-1 Plate approach'
        }, {
          step: '3',
          title: 'Transform',
          desc: 'Watch the bloating drop and energy rise by day 7'
        }].map((s, i) => <div key={i} className="text-center relative bg-cream" data-id="element-85">
              <div className="w-20 h-20 bg-forest text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-8 shadow-xl border-4 border-cream" data-id="element-86">
                {s.step}
              </div>
              <h3 className="font-playfair text-2xl font-bold mb-4 text-charcoal" data-id="element-87">
                {s.title}
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-88">{s.desc}</p>
            </div>)}
        </div>
      </section>

      {/* 8. PROOF / BELIEVABILITY */}
      <section className="bg-charcoal text-cream py-24 px-4 md:px-8 text-center" data-id="element-89">
        <div className="max-w-4xl mx-auto" data-id="element-90">
          <Clock className="w-16 h-16 text-terracotta mx-auto mb-8 opacity-90" data-id="element-91" />
          <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-10" data-id="element-92">
            Real Results, Fast.
          </h2>
          <blockquote className="text-2xl md:text-4xl font-playfair italic font-light leading-relaxed mb-8 text-gray-200" data-id="element-93">
            "By day 7, most women report their afternoon puffiness disappears
            and their morning energy returns. By day 14, the goal jeans fit
            again."
          </blockquote>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-24 px-4 md:px-8 max-w-3xl mx-auto" data-id="element-94">
        <h2 className="font-playfair text-4xl md:text-5xl font-bold text-center mb-16 text-charcoal" data-id="element-95">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6" data-id="element-96">
          {[{
          q: 'Do I need to cook?',
          a: "No, it's mostly assembly-based with minimal cooking."
        }, {
          q: 'How soon will I see results?',
          a: 'Many feel less bloated in the first 72 hours.'
        }, {
          q: 'Do I need special ingredients?',
          a: 'No, all components are common grocery store staples.'
        }, {
          q: 'Is this Keto or Paleo?',
          a: 'No, we focus on the Thermic Effect of Food, not restricting carbs or fats.'
        }].map((faq, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-97">
              <h4 className="font-bold text-xl mb-3 text-charcoal flex items-start gap-3" data-id="element-98">
                <span className="text-terracotta font-black" data-id="element-99">Q:</span> {faq.q}
              </h4>
              <p className="text-lg text-gray-600 flex items-start gap-3" data-id="element-100">
                <span className="text-forest font-black" data-id="element-101">A:</span> {faq.a}
              </p>
            </div>)}
        </div>
      </section>

      {/* 10. OFFER + PRICE BLOCK */}
      <section id="offer" className="bg-softGreen py-24 px-4 md:px-8 text-center border-t-8 border-forest" data-id="element-102">
        <div className="max-w-3xl mx-auto" data-id="element-103">
          <BookOpen className="w-16 h-16 text-forest mx-auto mb-8" data-id="element-104" />
          <h2 className="font-playfair text-5xl md:text-6xl font-bold text-charcoal mb-8" data-id="element-105">
            Reclaim Your Metabolism
          </h2>
          <p className="text-2xl text-gray-700 mb-10 leading-relaxed" data-id="element-106">
            Get the proven, practical blueprint. The full PDF eBook for a
            one-time payment of{' '}
            <span className="line-through text-gray-400 decoration-2" data-id="element-107">$27</span>{' '}
            <span className="font-bold text-terracotta text-3xl ml-2" data-id="element-108">
              $6.36
            </span>{' '}
            today.
          </p>
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100 max-w-2xl mx-auto" data-id="element-109">
            <CtaButton text="GET YOUR 15-MINUTE METABOLIC RESET NOW" subtext="Your digital download is delivered instantly." href="https://saludvitalintegral.com/cart/48610359673083:1" data-id="element-110" />
            <div className="mt-8 flex items-center justify-center gap-4 text-sm text-gray-500 font-medium" data-id="element-111">
              <span className="flex items-center gap-1" data-id="element-112">
                <Check className="w-4 h-4 text-forest" data-id="element-113" /> Secure Checkout
              </span>
              <span className="flex items-center gap-1" data-id="element-114">
                <Check className="w-4 h-4 text-forest" data-id="element-115" /> Instant Access
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-charcoal py-10 px-4 text-center" data-id="element-116">
        <div className="flex items-center justify-center gap-6 mb-4" data-id="element-117">
          <button onClick={() => setActiveModal('privacy')} className="text-gray-400 hover:text-white text-sm font-medium transition-colors underline underline-offset-2" data-id="element-118">
            Privacy Policy
          </button>
          <span className="text-gray-600" data-id="element-119">•</span>
          <button onClick={() => setActiveModal('terms')} className="text-gray-400 hover:text-white text-sm font-medium transition-colors underline underline-offset-2" data-id="element-120">
            Terms & Conditions
          </button>
        </div>
        <p className="text-gray-500 text-xs" data-id="element-121">
          © {new Date().getFullYear()} The 15-Minute Metabolic Reset. All
          rights reserved.
        </p>
      </footer>

      {activeModal === 'privacy' && <Modal title="Privacy Policy" content={privacyContent} onClose={() => setActiveModal(null)} data-id="element-122" />}
      {activeModal === 'terms' && <Modal title="Terms & Conditions" content={termsContent} onClose={() => setActiveModal(null)} data-id="element-123" />}
    </div>;
}